import { inject, Injectable } from "@angular/core";
import { TeamLeaderApi } from "../Api/TeamLeaderApi";
import { TeamLeaderState } from "./TeamLeaderState";
import { SearchTeamLeaderRequest } from "../Dto/TeamLeaderSearchRequest";
import { CreateTeamLeaderRequest } from "../Dto/TeamLeaderCreateRequest";
import { TeamLeaderListItem } from "../Dto/TeamLeaderModel";
import { UpdateTeamLeaderRequest } from "../Dto/TeamLeaderUpdateRequest";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class TeamLeaderEffects {
  private readonly teamLeaderApi = inject(TeamLeaderApi);
  private readonly teamLeaderState = inject(TeamLeaderState);

  async searchTeamLeaders(params: Partial<SearchTeamLeaderRequest> = {}): Promise<void> {
    const defaultParams: SearchTeamLeaderRequest = {
      page: 1,
      limit: 10,
      ...params,
    };

    this.teamLeaderState.setLoading(true);
    this.teamLeaderState.setError(null);
    this.teamLeaderState.setSearchParams(defaultParams);

    try {
      const response = await this.teamLeaderApi.searchTeamLeaders(defaultParams);
      this.teamLeaderState.setTeamLeaders(response.data, response.pagination);
    } catch (error: unknown) {
      this.teamLeaderState.setError(getBackendErrorMessage(error, "An unexpected error occurred"));
    } finally {
      this.teamLeaderState.setLoading(false);
    }
  }

  async getTeamLeaderById(id: string): Promise<TeamLeaderListItem | null> {
    this.teamLeaderState.setLoading(true);
    this.teamLeaderState.setError(null);

    try {
      return await this.teamLeaderApi.getTeamLeaderById(id);
    } catch (error: unknown) {
      this.teamLeaderState.setError(getBackendErrorMessage(error, "An unexpected error occurred"));
      return null;
    } finally {
      this.teamLeaderState.setLoading(false);
    }
  }

  async createTeamLeader(payload: CreateTeamLeaderRequest): Promise<TeamLeaderListItem> {
    this.teamLeaderState.setLoading(true);
    this.teamLeaderState.setError(null);

    try {
      const createdTeamLeader = await this.teamLeaderApi.createTeamLeader(payload);
      this.teamLeaderState.addTeamLeaderToState(createdTeamLeader);
      return createdTeamLeader;
    } catch (exception: unknown) {
      this.teamLeaderState.setError(getBackendErrorMessage(exception, "An unexpected error occurred"));
      throw createBackendError(exception, "An unexpected error occurred");
    } finally {
      this.teamLeaderState.setLoading(false);
    }
  }

  async updateTeamLeader(id: string, data: UpdateTeamLeaderRequest): Promise<TeamLeaderListItem> {
    this.teamLeaderState.setLoading(true);
    this.teamLeaderState.setError(null);

    try {
      const updatedTeamLeader = await this.teamLeaderApi.updateTeamLeader(id, data);
      if (!updatedTeamLeader) {
        throw new Error("Team leader not found.");
      }
      this.teamLeaderState.updateTeamLeaderInState(id, updatedTeamLeader);
      return updatedTeamLeader;
    } catch (exception: unknown) {
      this.teamLeaderState.setError(getBackendErrorMessage(exception, "An unexpected error occurred"));
      throw createBackendError(exception, "An unexpected error occurred");
    } finally {
      this.teamLeaderState.setLoading(false);
    }
  }

  async deleteTeamLeader(id: string): Promise<boolean> {
    this.teamLeaderState.setLoading(true);
    this.teamLeaderState.setError(null);

    try {
      await this.teamLeaderApi.deleteTeamLeader(id);
      this.teamLeaderState.removeTeamLeaderFromState(id);
      return true;
    } catch (exception: unknown) {
      this.teamLeaderState.setError(getBackendErrorMessage(exception, "An unexpected error occurred"));
      throw createBackendError(exception, "An unexpected error occurred");
    } finally {
      this.teamLeaderState.setLoading(false);
    }
  }

  async assignPartners(id: string, partnerIds: string[]): Promise<TeamLeaderListItem> {
    this.teamLeaderState.setLoading(true);
    this.teamLeaderState.setError(null);

    try {
      const updatedTeamLeader = await this.teamLeaderApi.assignPartners(id, partnerIds);
      if (!updatedTeamLeader) {
        throw new Error("Team leader not found.");
      }
      this.teamLeaderState.updateTeamLeaderInState(id, updatedTeamLeader);
      return updatedTeamLeader;
    } catch (exception: unknown) {
      this.teamLeaderState.setError(getBackendErrorMessage(exception, "An unexpected error occurred"));
      throw createBackendError(exception, "An unexpected error occurred");
    } finally {
      this.teamLeaderState.setLoading(false);
    }
  }
}
